package com.hyperether.goodjob.scenes.scheduler

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.absoluteOffset
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.layout.wrapContentSize
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.selection.DisableSelection
import androidx.compose.material.Icon
import androidx.compose.material.IconButton
import androidx.compose.material.Text
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Close
import androidx.compose.runtime.Composable
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.PointerIcon
import androidx.compose.ui.input.pointer.pointerHoverIcon
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.window.Dialog
import com.hyperether.goodjob.models.Contact
import com.hyperether.goodjob.models.Job
import com.hyperether.goodjob.models.Site
import com.hyperether.goodjob.resources.Res
import com.hyperether.goodjob.resources.chevron_left
import com.hyperether.goodjob.resources.chevron_right
import com.hyperether.goodjob.resources.clock_small
import com.hyperether.goodjob.resources.company
import com.hyperether.goodjob.resources.contact_information
import com.hyperether.goodjob.resources.date
import com.hyperether.goodjob.resources.day
import com.hyperether.goodjob.resources.end_time
import com.hyperether.goodjob.resources.location_details
import com.hyperether.goodjob.resources.map_job_item
import com.hyperether.goodjob.resources.month_capitalized
import com.hyperether.goodjob.resources.no_information
import com.hyperether.goodjob.resources.schedule
import com.hyperether.goodjob.resources.site
import com.hyperether.goodjob.resources.start_time
import com.hyperether.goodjob.resources.user_check_job_item
import com.hyperether.goodjob.resources.week
import com.hyperether.goodjob.scenes.components.Text18Bold
import com.hyperether.goodjob.scenes.components.clickableWithPointer
import com.hyperether.goodjob.theme.BlueApp
import com.hyperether.goodjob.theme.SchedulerBgColor
import com.hyperether.goodjob.util.DateTimeUtil
import com.hyperether.goodjob.util.JobStatusMap
import com.hyperether.goodjob.util.NestedComposeViewPort
import com.hyperether.goodjob.util.StatusColor
import com.hyperether.goodjob.util.toScaleDp
import com.hyperether.goodjob.util.toScaleSp
import com.hyperether.resources.stringResource
import org.jetbrains.compose.resources.painterResource


@Composable
fun PeriodChooser(state: MutableState<PeriodType>, changed: () -> Unit) {
    Row(modifier = Modifier.height(40.toScaleDp())) {
        Box(
            contentAlignment = Alignment.Center,
            modifier = Modifier
                .fillMaxHeight()
                .background(
                    SchedulerBgColor,
                    shape = RoundedCornerShape(
                        topStart = 10.toScaleDp(),
                        bottomStart = 10.toScaleDp()
                    )
                )
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null
                ) {
                    state.value = PeriodType.DAY
                    changed()
                }
                .padding(horizontal = 20.toScaleDp())
                .pointerHoverIcon(PointerIcon.Hand)
                .weight(0.33f)
        ) {
            DisableSelection {
                Text(
                    stringResource(Res.string.day),
                    color = if (state.value == PeriodType.DAY) BlueApp else Color(146, 144, 144),
                    fontSize = 14.toScaleSp(),
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
            }
        }
        Box(
            modifier = Modifier.fillMaxHeight().width(1.toScaleDp())
                .background(Color(231, 231, 231))
        )

        Box(
            contentAlignment = Alignment.Center,
            modifier = Modifier
                .fillMaxHeight()
                .background(SchedulerBgColor)
                .clickableWithPointer {
                    state.value = PeriodType.WEEK
                    changed()
                }
                .padding(horizontal = 20.toScaleDp())
                .weight(0.33f)
        ) {
            DisableSelection {
                Text(
                    stringResource(Res.string.week),
                    color = if (state.value == PeriodType.WEEK) BlueApp else Color(146, 144, 144),
                    fontSize = 14.toScaleSp(),
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
            }
        }
        Box(
            modifier = Modifier.fillMaxHeight().width(1.toScaleDp())
                .background(Color(231, 231, 231))
        )

        Box(
            contentAlignment = Alignment.Center,
            modifier = Modifier
                .fillMaxHeight()
                .background(
                    SchedulerBgColor,
                    shape = RoundedCornerShape(topEnd = 10.toScaleDp(), bottomEnd = 10.toScaleDp())
                )
                .clickableWithPointer {
                    state.value = PeriodType.MONTH
                    changed()
                }
                .padding(horizontal = 20.toScaleDp())
                .weight(0.33f)
        ) {
            DisableSelection {
                Text(
                    stringResource(Res.string.month_capitalized),
                    color = if (state.value == PeriodType.MONTH) BlueApp else Color(146, 144, 144),
                    fontSize = 14.toScaleSp(),
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
            }
        }
    }
}

@Composable
fun PeriodSwitcher(
    text: String,
    prev: () -> Unit,
    next: () -> Unit,
    modifier: Modifier = Modifier
) {
    Row(
        modifier.padding(horizontal = 20.toScaleDp(), vertical = 8.toScaleDp()),
        verticalAlignment = Alignment.CenterVertically
    ) {
        IconButton(
            onClick = {
                prev()
            }
        ) {
            Icon(
                modifier = Modifier
                    .size(24.toScaleDp())
                    .pointerHoverIcon(PointerIcon.Hand),
                painter = painterResource(Res.drawable.chevron_left),
                contentDescription = "Previous date or week"
            )
        }
        Text18Bold(text, modifier = Modifier.weight(1f))
        IconButton(
            onClick = {
                next()
            }
        ) {
            Icon(
                modifier = Modifier
                    .size(24.toScaleDp())
                    .pointerHoverIcon(PointerIcon.Hand),
                painter = painterResource(Res.drawable.chevron_right),
                contentDescription = "Next date or week"
            )
        }
    }
}

@Composable
fun SchedulerJobHover(
    sites: Map<String, Site>,
    job: Job,
    x: androidx.compose.ui.unit.Dp,
    y: androidx.compose.ui.unit.Dp
) {
    Box(
        modifier = Modifier
            .absoluteOffset(x, y)
            .wrapContentSize()
            .padding(16.toScaleDp())
            .background(Color.White, shape = RoundedCornerShape(12.toScaleDp()))
            .border(1.toScaleDp(), Color.LightGray, RoundedCornerShape(12.toScaleDp()))
    ) {

        Column(modifier = Modifier.padding(12.toScaleDp())) {
            Row(
                modifier = Modifier.padding(end = 40.toScaleDp()).height(30.toScaleDp()),
                verticalAlignment = Alignment.CenterVertically
            ) {
                job.jobTitle?.let {
                    Box(
                        modifier = Modifier.height(30.toScaleDp())
                            .widthIn(max = 400.toScaleDp()),
                        contentAlignment = Alignment.CenterStart
                    ) {
                        Text(
                            text = it,
                            color = Color.Black,
                            fontWeight = FontWeight.Bold,
                            fontSize = 18.toScaleSp(),
                            overflow = TextOverflow.Ellipsis,
                            maxLines = 1,
                        )
                    }
                }
                Spacer(modifier = Modifier.width(16.toScaleDp()))
                Box(
                    modifier = Modifier
                        .size(16.toScaleDp())
                        .background(
                            color = StatusColor.getStatusColor(job.status),
                            shape = CircleShape
                        )
                )
            }
            Spacer(modifier = Modifier.height(8.toScaleDp()))
            Row(
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier.padding(bottom = 4.toScaleDp())
            ) {
                Image(
                    modifier = Modifier.size(20.toScaleDp()),
                    painter = painterResource(Res.drawable.user_check_job_item),
                    contentDescription = null,
                )
                Spacer(modifier = Modifier.width(8.toScaleDp()))
                job.id?.let { id ->
                    sites[id]?.let {
                        Text(
                            text = it.siteName ?: it.personName ?: "",
                            color = BlueApp,
                            fontSize = 14.toScaleSp(),
                            lineHeight = 18.toScaleSp()
                        )
                    }
                }
            }
            Row(
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier.padding(bottom = 4.toScaleDp())
            ) {
                Image(
                    modifier = Modifier.size(24.toScaleDp()),
                    painter = painterResource(Res.drawable.map_job_item),
                    contentDescription = null,
                )
                Spacer(modifier = Modifier.width(8.toScaleDp()))
                job.contactSiteId?.let {
                    Text(
                        text = sites[job.id]?.addressObject?.address ?: "",
                        color = BlueApp,
                        fontSize = 14.toScaleSp(),
                        lineHeight = 18.toScaleSp()
                    )
                }
            }
            Row(
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier.padding(bottom = 4.toScaleDp())
            ) {
                Image(
                    modifier = Modifier.size(24.toScaleDp()),
                    painter = painterResource(Res.drawable.clock_small),
                    contentDescription = null,
                )
                Spacer(modifier = Modifier.width(8.toScaleDp()))
                job.start_at?.let { start ->
                    job.end_at?.let { end ->
                        Text(
                            text = "$start - $end",
                            color = Color.Black,
                            fontSize = 14.toScaleSp(),
                            lineHeight = 18.toScaleSp()
                        )
                    }
                }
            }
        }
    }
}

@Composable
fun SchedulerJobWeeklyPopUp(
    sites: Map<String, Site>,
    job: Job,
    onDismissRequest: () -> Unit
) {
    Dialog(onDismissRequest = onDismissRequest) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .padding(16.toScaleDp())
                .background(Color.White, shape = RoundedCornerShape(12.toScaleDp()))
                .border(1.toScaleDp(), Color.LightGray, RoundedCornerShape(12.toScaleDp()))
        ) {
            Box(
                modifier = Modifier.fillMaxWidth(),
                contentAlignment = Alignment.TopEnd
            ) {
                IconButton(onClick = onDismissRequest) {
                    Icon(
                        imageVector = Icons.Default.Close,
                        contentDescription = "Close",
                        tint = Color.Gray
                    )
                }
            }
            Column(
                modifier = Modifier.fillMaxWidth()
            ) {
                Row {
                    Spacer(modifier = Modifier.width(10.toScaleDp()))

                    Box(
                        modifier = Modifier
                            .fillMaxWidth()

                    ) {
                        Column(modifier = Modifier.padding(12.toScaleDp())) {
                            Row(
                                modifier = Modifier.fillMaxWidth().padding(end = 40.toScaleDp())
                                    .height(30.toScaleDp()),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                job.jobTitle?.let {
                                    Box(
                                        modifier = Modifier.height(30.toScaleDp()),
                                        contentAlignment = Alignment.CenterStart
                                    ) {
                                        Text(
                                            text = it,
                                            color = Color.Black,
                                            fontWeight = FontWeight.Bold,
                                            fontSize = 18.toScaleSp(),
                                            maxLines = 1,
                                            overflow = TextOverflow.Ellipsis
                                        )
                                    }
                                }
                                Spacer(modifier = Modifier.width(16.toScaleDp()))
                                Box(
                                    modifier = Modifier
                                        .size(16.toScaleDp())
                                        .background(
                                            color = StatusColor.getStatusColor(job.status),
                                            shape = CircleShape
                                        )
                                )
                            }
                            Spacer(modifier = Modifier.height(8.toScaleDp()))
                            Row(
                                verticalAlignment = Alignment.CenterVertically,
                                modifier = Modifier.padding(bottom = 4.toScaleDp())
                            ) {
                                Image(
                                    painter = painterResource(Res.drawable.user_check_job_item),
                                    contentDescription = null,
                                )
                                Spacer(modifier = Modifier.width(8.toScaleDp()))
                                job.id?.let { id ->
                                    sites[id]?.let {
                                        Text(
                                            text = it.siteName ?: it.personName ?: stringResource(
                                                Res.string.no_information
                                            ),
                                            color = BlueApp,
                                            fontSize = 14.toScaleSp(),
                                            lineHeight = 18.toScaleSp()
                                        )
                                    } ?: run {
                                        Text(
                                            text = stringResource(Res.string.no_information),
                                            color = Color.Black,
                                            fontSize = 14.toScaleSp(),
                                            lineHeight = 18.toScaleSp()
                                        )
                                    }
                                }
                            }
                            Row(
                                verticalAlignment = Alignment.CenterVertically,
                                modifier = Modifier.padding(bottom = 4.toScaleDp())
                            ) {
                                Image(
                                    painter = painterResource(Res.drawable.map_job_item),
                                    contentDescription = null,
                                )
                                Spacer(modifier = Modifier.width(8.toScaleDp()))
                                job.contactSiteId?.let {
                                    Text(
                                        text = sites[job.id]?.addressObject?.address
                                            ?: stringResource(Res.string.no_information),
                                        color = if (sites[job.id]?.addressObject?.address?.isNotBlank() == true) BlueApp else Color.Black,
                                        fontSize = 14.toScaleSp(),
                                        lineHeight = 18.toScaleSp()
                                    )
                                }
                            }
                            Row(
                                verticalAlignment = Alignment.CenterVertically,
                                modifier = Modifier.padding(bottom = 4.toScaleDp())
                            ) {
                                Image(
                                    painter = painterResource(Res.drawable.clock_small),
                                    contentDescription = null,
                                )
                                Spacer(modifier = Modifier.width(8.toScaleDp()))
                                job.start_at?.let { start ->
                                    job.end_at?.let { end ->
                                        Text(
                                            text = "$start - $end",
                                            color = Color.Black,
                                            fontSize = 14.toScaleSp(),
                                            lineHeight = 18.toScaleSp()
                                        )
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun SchedulerJobWeeklyPopUpDetailed(
    sites: Map<String, Site>,
    contact: Contact?,
    job: Job,
    onDismissRequest: () -> Unit
) {
    NestedComposeViewPort {
        val jobStatusMap = JobStatusMap()
        Dialog(onDismissRequest = onDismissRequest) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(16.toScaleDp())
                    .background(Color.White, shape = RoundedCornerShape(12.toScaleDp()))
                    .border(1.toScaleDp(), Color.LightGray, RoundedCornerShape(12.toScaleDp()))
            ) {
                Box(
                    modifier = Modifier.fillMaxWidth(),
                    contentAlignment = Alignment.TopEnd
                ) {
                    IconButton(onClick = onDismissRequest) {
                        Icon(
                            modifier = Modifier
                                .size(20.toScaleDp())
                                .pointerHoverIcon(PointerIcon.Hand),
                            imageVector = Icons.Default.Close,
                            contentDescription = "Close",
                            tint = Color.Gray
                        )
                    }
                }
                Column(modifier = Modifier.padding(12.toScaleDp())) {
                    job.jobTitle?.let {
                        Box(
                            modifier = Modifier.height(30.toScaleDp())
                                .padding(end = 20.toScaleDp()),
                            contentAlignment = Alignment.CenterStart
                        ) {
                            Text(
                                text = it,
                                color = Color.Black,
                                fontWeight = FontWeight.Bold,
                                fontSize = 20.toScaleSp(),
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis
                            )
                        }
                    }
                    Spacer(modifier = Modifier.height(8.toScaleDp()))
                    Box(
                        modifier = Modifier
                            .width(155.toScaleDp())
                            .height(30.toScaleDp())
                            .background(
                                color = StatusColor.getStatusColor(job.status),
                                shape = RoundedCornerShape(30.toScaleDp())
                            ),
                        contentAlignment = Alignment.Center
                    ) {
                        val status = jobStatusMap.entries.find { it.value == job.status }?.key
                        status?.let { Text(it, maxLines = 1, fontSize = 12.toScaleSp()) }
                    }
                    Spacer(modifier = Modifier.height(8.toScaleDp()))
                    Text(
                        text = stringResource(Res.string.contact_information),
                        color = Color.Black,
                        fontWeight = FontWeight.Bold,
                        fontSize = 17.toScaleSp(),
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                    if (contact?.name.isNullOrEmpty() && contact?.phone.isNullOrEmpty() && contact?.email.isNullOrEmpty()) {
                        Text(
                            text = stringResource(Res.string.no_information),
                            color = Color.Black,
                            fontSize = 14.toScaleSp(),
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis
                        )
                    } else {
                        contact?.name?.let {
                            Text(
                                text = it,
                                color = Color.Black,
                                fontSize = 14.toScaleSp(),
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis
                            )
                        }

                        contact?.phone?.let {
                            Text(
                                text = it,
                                color = Color.Black,
                                fontSize = 14.toScaleSp(),
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis
                            )
                        }

                        contact?.email?.let {
                            Text(
                                text = it,
                                color = Color.Black,
                                fontSize = 14.toScaleSp(),
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis
                            )
                        }
                    }
                    Spacer(modifier = Modifier.height(8.toScaleDp()))

                    Text(
                        text = stringResource(Res.string.location_details),
                        color = Color.Black,
                        fontWeight = FontWeight.Bold,
                        fontSize = 17.toScaleSp(),
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )

                    sites[job.id]?.addressObject?.address?.let {
                        Text(
                            text = it,
                            color = Color.Black,
                            fontSize = 14.toScaleSp(),
                            maxLines = 2,
                            overflow = TextOverflow.Ellipsis
                        )
                    }

                    if (sites[job.id]?.siteName.isNullOrEmpty() && contact?.name.isNullOrEmpty()) {
                        Text(
                            text = stringResource(Res.string.no_information),
                            color = Color.Black,
                            fontSize = 14.toScaleSp(),
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis
                        )
                    } else {
                        sites[job.id]?.siteName?.let {
                            Row {
                                Text(
                                    text = "${stringResource(Res.string.site)}: ",
                                    fontSize = 14.toScaleSp(),
                                    maxLines = 1,
                                    overflow = TextOverflow.Ellipsis
                                )
                                Text(
                                    text = it,
                                    color = Color.Black,
                                    fontSize = 14.toScaleSp(),
                                    maxLines = 1,
                                    overflow = TextOverflow.Ellipsis
                                )
                            }
                        }

                        contact?.name?.let {
                            Row {
                                Text(
                                    text = "${stringResource(Res.string.company)}: ",
                                    fontSize = 14.toScaleSp(),
                                    maxLines = 1,
                                    overflow = TextOverflow.Ellipsis
                                )
                                Text(
                                    text = it,
                                    color = Color.Black,
                                    fontSize = 14.toScaleSp(),
                                    maxLines = 1,
                                    overflow = TextOverflow.Ellipsis
                                )
                            }
                        }
                    }

                    Spacer(modifier = Modifier.height(8.toScaleDp()))

                    Text(
                        text = stringResource(Res.string.schedule),
                        color = Color.Black,
                        fontWeight = FontWeight.Bold,
                        fontSize = 17.toScaleSp(),
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )

                    job.startDate?.let {
                        Row {
                            Text(
                                text = "${stringResource(Res.string.date)}: ",
                                fontSize = 14.toScaleSp(),
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis
                            )
                            Text(
                                text = DateTimeUtil.jobListDateFormatter(it),
                                color = Color.Black,
                                fontSize = 14.toScaleSp(),
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis
                            )
                        }
                    }

                    job.start_at?.let {
                        Row {
                            Text(
                                text = "${stringResource(Res.string.start_time)}: ",
                                fontSize = 14.toScaleSp(),
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis
                            )
                            Text(
                                text = it,
                                color = Color.Black,
                                fontSize = 14.toScaleSp(),
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis
                            )
                        }
                    }

                    job.end_at?.let {
                        Row {
                            Text(
                                text = "${stringResource(Res.string.end_time)}: ",
                                fontSize = 14.toScaleSp(),
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis
                            )
                            Text(
                                text = it,
                                color = Color.Black,
                                fontSize = 14.toScaleSp(),
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis
                            )
                        }
                    }
                }
            }
        }
    }
}